<template>
  <v-container fluid py-0>
    <v-row>
      <v-col cols="12" class="py-0 mb-0">
        <v-radio-group
          v-model="selectedSupplierType"
          row
          :disabled="$apollo.loading"
          class="my-0 py-0"
          @change="onServiceTypeChange"
          dense
        >
          <v-radio
            v-for="sType in supplierTypes"
            :key="sType"
            :value="sType"
            hide-details
          >
            <template v-slot:label>
              <v-icon left>{{ supplierTypeIcon(sType) }}</v-icon>
            </template>
          </v-radio>
        </v-radio-group>
      </v-col>
    </v-row>
    <v-row align="center">
      <v-col cols="6">
        <v-text-field
          label="Supplier"
          v-model="supplier"
          :outlined="outlinedInputs"
          hide-details
          clearable
          v-on:keyup.enter="onSearch"
          dense
        ></v-text-field>
      </v-col>
      <v-col cols="6">
        <v-text-field
          label="Service"
          v-model="service"
          :outlined="outlinedInputs"
          hide-details
          clearable
          v-on:keyup.enter="onSearch"
          dense
        ></v-text-field>
      </v-col>
      <v-col cols="9" class="py-0">
        <destination-list-select
          v-model="selectedDestinationId"
          :outlined="outlinedInputs"
          hide-details
          dense
        ></destination-list-select>
      </v-col>
      <v-col
        ><v-btn
          color="primary"
          :disabled="!isValid"
          @click="onSearch"
          :loading="$apollo.loading"
          block
          dense
          class="ma-0"
          >Search</v-btn
        ></v-col
      >
    </v-row>

    <v-divider></v-divider>
    <v-row>
      <v-col cols="12">
        <v-data-iterator
          :items="items"
          item-key="id"
          :items-per-page="10"
          :page.sync="page"
        >
          <template v-slot:default="{ items }">
            <v-expansion-panels :disabled="$apollo.loading" v-model="panel">
              <v-expansion-panel v-for="item in items" :key="item.id">
                <v-expansion-panel-header>
                  <v-row align="center">
                    <v-tooltip bottom z-index="300" v-if="item.imageURL">
                      <template v-slot:activator="{ on }">
                        <v-avatar size="32" v-on="on" class="mr-2">
                          <v-img
                            v-if="item.imageURL"
                            :src="item.imageURL"
                          ></v-img>
                        </v-avatar>
                      </template>
                      <v-container style="max-width: 500px">
                        <v-row>
                          <v-col
                            cols="12"
                            v-if="item.imageURL && item.imageURL != ''"
                          >
                            <v-img
                              contain
                              :src="item.imageURL"
                              max-height="200"
                            ></v-img>
                          </v-col>
                          <v-col cols="12">
                            <div
                              class="max-lines"
                              v-html="item.description"
                            ></div>
                          </v-col>
                        </v-row>
                      </v-container>
                    </v-tooltip>
                    <v-avatar v-else size="32" class="mr-2">
                      <v-icon large> mdi-information</v-icon>
                    </v-avatar>
                    <strong>{{ item.name }} ({{ item.code }})</strong>
                  </v-row>
                </v-expansion-panel-header>
                <v-expansion-panel-content class="px-0">
                  <suppliers-search-supplier-item
                    dense
                    :supplier="item"
                  ></suppliers-search-supplier-item>
                  <v-row>
                    <v-col cols="12">
                      <v-row justify="center">
                        <v-btn text color="primary" @click="onEdit(item)">
                          <v-icon left small>mdi-pencil</v-icon>Edit
                          {{ item.name }}
                        </v-btn>
                      </v-row>
                    </v-col>
                  </v-row>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </template>
        </v-data-iterator>
      </v-col>
    </v-row>
    <div v-if="debug">
      <code>items:{{ destinationId }}</code>
    </div>
  </v-container>
</template>

<script>
import gql from "graphql-tag";
import { SUPPLIER_TYPES } from "../../../../constants/supplierTypes";
import { SupplierTypeIconMixin } from "../../../../mixins/SupplierTypeIconMixin";
import DestinationListSelect from "../../shared/DestinationListSelect";
import { removeGQLTypename } from "../../../../helpers";
import SuppliersSearchSupplierItem from "./SuppliersSearchSupplierItem";

const SUPPLIERS_WITH_SERVICES = gql`
  query suppliersWithServices(
    $supplierName: String
    $serviceName: String
    $destinationId: ID
    $serviceType: GtServiceType
  ) {
    suppliers(
      first: 100
      filter: {
        name_contains: $supplierName
        destinationId: $destinationId
        service_type: $serviceType
        not_in_use_eq: false
      }
      orderBy: { fieldName: "name", direction: ASC }
    ) {
      edges {
        node {
          id
          name
          serviceType
          imageURL
          description
          code
          services(
            filter: { not_in_use_eq: false, name_contains: $serviceName }
            orderBy: { fieldName: "name", direction: ASC }
          ) {
            edges {
              node {
                id
                name
                code
                imageURL
                description
                extras {
                  edges {
                    node {
                      id
                      name
                      typeId
                      isMandatory
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;
export default {
  name: "suppliers-search",

  props: {
    destinationId: {
      type: String,
      default: () => "",
    },
  },

  mixins: [SupplierTypeIconMixin],

  components: {
    DestinationListSelect,
    SuppliersSearchSupplierItem,
  },

  data() {
    return {
      debug: false,
      panel: null,
      page: 1,
      selectedSupplierType: "ACCOMMODATION",
      supplierTypes: SUPPLIER_TYPES,
      selectedDestinationId: this.destinationId ? this.destinationId : "",
      supplier: "",
      service: "",
      searchParams: null,
    };
  },

  computed: {
    items() {
      return this.suppliers && this.suppliers.edges
        ? this.suppliers.edges
            .map(removeGQLTypename)
            .filter((item) => item.services.edges.length > 0)
        : [];
    },
    outlinedInputs() {
      return this.$store.getters.localSettings("outlinedInputs");
    },
    isValid() {
      return true;
      //return this.supplier != "" || this.service != "";
    },
  },

  watch: {
    destinationId(newDest) {
      this.selectedDestinationId = newDest;
      this.onSearch();
    },
  },

  methods: {
    onEdit(payload) {
      this.$emit("supplier-edit", payload);
      this.onSearch();
    },
    onSearch() {
      this.searchParams = {
        destinationId: this.selectedDestinationId,
        supplierName: this.supplier,
        serviceName: this.service,
        serviceType: this.selectedSupplierType,
      };
      this.panel = null;
    },
    onServiceTypeChange() {
      this.service = "";
      this.supplier = "";
      this.onSearch();
    },
  },

  apollo: {
    suppliers: {
      query: SUPPLIERS_WITH_SERVICES,
      variables() {
        return {
          ...this.searchParams,
        };
      },
      skip() {
        return !this.searchParams;
      },
      fetchPolicy: "network-only",
      result() {
        this.page = 1;
      },
    },
  },
  mounted() {
    //this.onSearch();
  },
};
</script>

<style></style>



// WEBPACK FOOTER //
// src/components/gt/templates/builder/SuppliersSearch.vue